// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Shared utils for Display Items.
import * as utils from './utils.jsx';

import { ItemCTAContent, ItemRating } from './items/item-components';

// Model objects.
import DisplayItem from '../../model/entity/DisplayItem/DisplayItem.js';
import PinnedDisplayItem from '../../model/entity/PinnedDisplayItem.js';
import itemFactory from '../../../apps/UgcWall/components/ItemFactory';

/**
 * <pre>
 * Display Item > Overlay related React components.
 *
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.DisplayItems.Overlays
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * Media card overlay Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Overlays
 */
class OverlayMediacard extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const item = this.props.item,
      itemOpts = this.props.itemOpts || {},
      moduleOpts = this.props.moduleOpts || {},
      templateOpts = this.props.templateOpts || {};

    const displayItemModel = {
      'data-ngx-tags': item.meta.tags,
    };

    const _networkIcon = utils.getNetworkIcon(item.meta.network);

    const _itemDetailAsset = utils.getItemDetailAsset(item, itemOpts, this.props.assetUrl, this.props.assetUgcUrl);

    const _mainContent = utils.getItemContent({
      item,
      type:                'main',
      onExternalLinkClick: this.props.onExternalLinkClick,
    });
    const _secondaryContent = utils.getItemContent({ item, type: 'secondary' });

    const _profile = utils.getItemProfile({ item, onExternalLinkClick: this.props.onExternalLinkClick });

    const _rating = getItemRating(item);
    const _timestamp = utils.getItemTimestamp({ item, onExternalLinkClick: this.props.onExternalLinkClick });

    // Define any additional actions to output alongside the network/sharing actions.
    const _additionalActions = utils.getAdditionalActions(item);

    let _networkActions;
    if (item.actions && item.actions.network) {
      _networkActions = utils.getNetworkActions(
        item,
        itemOpts,
        _additionalActions,
        true,
        this.props.onExternalLinkClick
      );
    }

    let _platformActions;
    if (item.actions && item.actions.platform) {
      _platformActions = utils.getPlatformActions(item, itemOpts);
    }

    // Only show sharing actions, if we have no already show network actions.
    let _sharingActions;
    if (item.actions && item.actions.sharing && !item.actions.network) {
      _sharingActions = utils.getSharingActions(
        item,
        itemOpts,
        _additionalActions,
        'mode.inline',
        this.props.onExternalLinkClick
      );
    }

    // CTA content.
    let _ctaContent;
    if (moduleOpts && moduleOpts.ctaDisplayItemRender === 'overlay') {
      _ctaContent = getCTAContent(item, this.props.assetUrl, this.props.assetUgcUrl);
    }

    const className = utils.getClassNamesForItem({ item, templateOpts, itemOpts, isOverlay: true, tabIndex: 0 });

    return (
      <div {...displayItemModel} className={className} tabIndex={0}>
        <div className="xItemInner">
          <div className="xItemContent">
            {_itemDetailAsset}
            {_mainContent}
            {_secondaryContent}
            <If condition={!_.isEmpty(item.originalContent)}>
              <div className="xOriginalContainer">
                {itemFactory({
                  item:                item.originalContent,
                  itemOpts:            this.props.itemOpts,
                  templateOpts:        this.props.templateOpts,
                  moduleOpts:          this.props.moduleOpts,
                  assetUrl:            this.props.assetUrl,
                  assetUgcUrl:         this.props.assetUgcUrl,
                  onExternalLinkClick: this.props.onExternalLinkClick,
                  onClick:             _.noop,
                  isOverlay:           true,
                  tabIndex:            -1, // disable quoted container from gaining focus
                })}
              </div>
            </If>
            {_timestamp}
            {_networkIcon}
            {_networkActions}
            {_sharingActions}
            {_rating}
            {_profile}
            {_platformActions}
          </div>
          {_ctaContent}
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
OverlayMediacard.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(DisplayItem),
    PropTypes.instanceOf(PinnedDisplayItem),
  ]),
};

/**
 * React default props.
 *
 * @type {Object}
 */
OverlayMediacard.defaultProps = {};

export { OverlayMediacard };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Content card overlay Display Item.
 *
 * @namespace
 * @memberOf Common.Components.DisplayItems.Overlays
 */
class OverlayContentcard extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const { item, itemOpts, templateOpts, moduleOpts, onExternalLinkClick } = this.props;

    const displayItemModel = {
      'data-ngx-tags': item.meta.tags,
    };

    const _networkIcon = utils.getNetworkIcon(item.meta.network);

    const _itemDetailAsset = utils.getItemDetailAsset(
      item,
      itemOpts,
      this.props.assetUrl,
      this.props.assetUgcUrl,
      _.get(moduleOpts, 'autoPlayConfig')
    );
    const _mainContent = utils.getItemContent({ item, type: 'main', onExternalLinkClick });
    const _secondaryContent = utils.getItemContent({ item, type: 'secondary' });

    const _profile = utils.getItemProfile({ item, onExternalLinkClick });

    const _rating = getItemRating(item);
    const _timestamp = utils.getItemTimestamp({ item, onExternalLinkClick });

    // Define any additional actions to output alongside the network/sharing actions.
    const _additionalActions = utils.getAdditionalActions(item);

    let _networkActions;
    if (item.actions.network) {
      _networkActions = utils.getNetworkActions(item, itemOpts, _additionalActions, true, onExternalLinkClick);
    }

    let _platformActions;
    if (item.actions.platform) {
      _platformActions = utils.getPlatformActions(item, itemOpts);
    }

    // Only show sharing actions, if we have no already show network actions.
    let _sharingActions;
    if (item.actions.sharing && !item.actions.network) {
      _sharingActions = utils.getSharingActions(item, itemOpts, _additionalActions, 'mode.inline', onExternalLinkClick);
    }

    // CTA content.
    let _ctaContent;
    if (moduleOpts && moduleOpts.ctaDisplayItemRender === 'overlay') {
      _ctaContent = getCTAContent(item, this.props.assetUrl, this.props.assetUgcUrl);
    }

    const className = utils.getClassNamesForItem({ item, templateOpts, itemOpts, isOverlay: true, tabIndex: 0 });
    return (
      <div {...displayItemModel} className={className} tabIndex={0}>
        <div className="xItemInner">
          <div className="xItemContent">
            {_profile}
            {_mainContent}
            {_secondaryContent}
            <If condition={!_.isEmpty(item.originalContent)}>
              <div className="xOriginalContainer">
                {itemFactory({
                  item:                item.originalContent,
                  itemOpts:            this.props.itemOpts,
                  templateOpts:        this.props.templateOpts,
                  moduleOpts:          this.props.moduleOpts,
                  assetUrl:            this.props.assetUrl,
                  assetUgcUrl:         this.props.assetUgcUrl,
                  onExternalLinkClick: this.props.onExternalLinkClick,
                  onClick:             _.noop,
                  isOverlay:           true,
                  tabIndex:            -1, // disable quoted container from gaining focus
                })}
              </div>
            </If>
            {_rating}
            {_timestamp}
            {_platformActions}
            {_networkIcon}
            {_networkActions}
            {_sharingActions}
            {_itemDetailAsset}
          </div>
          {_ctaContent}
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem object.
 */
OverlayContentcard.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(DisplayItem),
    PropTypes.instanceOf(PinnedDisplayItem),
  ]),
};

/**
 * React default props.
 *
 * @type {Object}
 */
OverlayContentcard.defaultProps = {
  templateOpts:        {},
  itemOpts:            {},
  moduleOpts:          {},
  onExternalLinkClick: PropTypes.func,
};

export { OverlayContentcard };

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private utility functions */

/**
 * Returns item's CTA content if set
 *
 * @private
 *
 * @param item: {object} DisplayItem
 * @param assetUrl {string} the base path for an asset url
 * @param assetUgcUrl {string} the base path for an asset ugc url
 * @returns {React} CTA content
 */
function getCTAContent(item, assetUrl, assetUgcUrl) {
  let _ctaContent;
  if (utils.itemHasCTA(item)) {
    _ctaContent = <ItemCTAContent cta={item.cta} assetUrl={assetUrl} assetUgcUrl={assetUgcUrl} />;
  }

  return _ctaContent;
}

/**
 * Returns a rating component.
 *
 * @private
 *
 * @param item : {object} display item
 * @return {React} Rating component
 */
function getItemRating(item) {
  let _rating;

  if (item.review.rating) {
    _rating = (
      <ItemRating rating={item.review.rating} range={item.review.ratingRange} likes={item.review.reviewLikes} />
    );
  }

  return _rating;
}
